import {
    Alert,
    Box,
    Button,
    ColumnLayout,
    FormField,
    Modal,
    Select,
    SpaceBetween,
} from '@amzn/awsui-components-react';
import React, { useState } from 'react';
import CustomTag from 'src/models/accounting-calendar/CustomTag';
import { DeleteScope } from 'src/models/accounting-calendar/CustomTagEnums';
import ATPHorizontalRadioGroup from 'src/components/shared/ATPHorizontalRadioGroup';
import { MONTH_OPTIONS, buildYearOptions } from 'src/utils/accountingCalendarUtils';

interface Props {
    tag: CustomTag;
    deleting: boolean;
    /** Server-side error from the last delete attempt, shown in the dialog. */
    deleteError?: string;
    onConfirm: (scope: DeleteScope, targetMonth?: string) => void;
    onClose: () => void;
}

/**
 * Confirms deletion. One-time tags delete directly (SINGLE). Recurring tags offer a choice:
 * delete the whole rule (RECURRENCE) or skip a single month (SINGLE + targetMonth as YYYY-MM).
 */
export default function DeleteTagDialog(props: Props) {
    const { tag, deleting, deleteError, onConfirm, onClose } = props;
    const [scope, setScope] = useState<DeleteScope>(tag.isRecurring ? DeleteScope.RECURRENCE : DeleteScope.SINGLE);
    const [targetMonth, setTargetMonth] = useState<string>('');
    const [targetYear, setTargetYear] = useState<string>('');

    const yearOptions = buildYearOptions();

    // Single-month deletion requires both a month and year. The Delete button stays disabled
    // until both are chosen, so there's no need for a click-time validation error here.
    const missingMonthSelection =
        tag.isRecurring && scope === DeleteScope.SINGLE && (!targetMonth || !targetYear);

    function handleConfirm() {
        if (tag.isRecurring && scope === DeleteScope.SINGLE) {
            onConfirm(DeleteScope.SINGLE, `${targetYear}-${targetMonth}`);
            return;
        }
        onConfirm(scope);
    }

    return (
        <Modal
            data-testid="deleteTagDialog"
            visible={true}
            header="Delete tag"
            size="medium"
            onDismiss={onClose}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" disabled={deleting} onClick={onClose}>Cancel</Button>
                        <Button variant="primary" className="confirmDeleteButton" disabled={deleting || missingMonthSelection} onClick={handleConfirm}>
                            {deleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="m">
                {deleteError && <Alert type="error">{deleteError}</Alert>}
                {!tag.isRecurring && (
                    <div>Delete the tag "{tag.tagEventName}"? This cannot be undone.</div>
                )}
                {tag.isRecurring && (
                    <React.Fragment>
                        <div>"{tag.tagEventName}" is a recurring tag. What would you like to delete?</div>
                        <ATPHorizontalRadioGroup
                            value={scope}
                            onChange={(value: string) => setScope(value as DeleteScope)}
                            items={[
                                { label: 'The entire recurring rule', value: DeleteScope.RECURRENCE },
                                { label: 'Just one month', value: DeleteScope.SINGLE },
                            ]}
                        />
                        {scope === DeleteScope.SINGLE && (
                            <FormField label="Month to remove">
                                <ColumnLayout columns={2}>
                                    <Select
                                        selectedOption={targetMonth
                                            ? { label: MONTH_OPTIONS[Number(targetMonth) - 1].label, value: targetMonth }
                                            : null}
                                        options={MONTH_OPTIONS}
                                        onChange={({ detail }) => setTargetMonth(detail.selectedOption.value as string)}
                                        placeholder="Month"
                                    />
                                    <Select
                                        selectedOption={targetYear ? { label: targetYear, value: targetYear } : null}
                                        options={yearOptions}
                                        onChange={({ detail }) => setTargetYear(detail.selectedOption.value as string)}
                                        placeholder="Year"
                                    />
                                </ColumnLayout>
                            </FormField>
                        )}
                    </React.Fragment>
                )}
            </SpaceBetween>
        </Modal>
    );
}
